import type {
  CanonicalTreeConnectorPoint,
  CanonicalTreeConnectorRectangle,
  CanonicalTreeConnectorRoot,
  CanonicalTreeConnectorRouteStatus,
} from './CanonicalTreeConnectorCorridorAuthority';

export interface CanonicalTreeLateralConnectorInput {
  readonly key: string;
  readonly fromMemberId: string;
  readonly toMemberId: string;
  readonly root:
    CanonicalTreeConnectorRoot;
  readonly from:
    CanonicalTreeConnectorPoint;
  readonly to:
    CanonicalTreeConnectorPoint;
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly clearance: number;
  readonly obstacles:
    readonly CanonicalTreeConnectorRectangle[];
}

export interface CanonicalTreeLateralConnectorResult {
  readonly status:
    CanonicalTreeConnectorRouteStatus;
  readonly key: string;
  readonly root:
    CanonicalTreeConnectorRoot;
  readonly fromMemberIds:
    readonly string[];
  readonly toMemberId: string;
  readonly points:
    readonly CanonicalTreeConnectorPoint[];
  readonly corridorY:
    number | null;
  readonly rejectedObstacleMemberIds:
    readonly string[];
}

const TOLERANCE = 0.001;
const MAX_ATTEMPTS = 24;

const finite = (
  value: number,
  name: string,
): number => {
  if (!Number.isFinite(value)) {
    throw new Error(
      `Invalid lateral connector value: ${name}.`,
    );
  }

  return value;
};

const nonNegative = (
  value: number,
  name: string,
): number => {
  const checked = finite(value, name);

  if (checked < 0) {
    throw new Error(
      `Lateral connector value ${name} must be non-negative.`,
    );
  }

  return checked;
};

const segmentIntersectsRectangle = (
  start:
    CanonicalTreeConnectorPoint,
  end:
    CanonicalTreeConnectorPoint,
  rectangle:
    CanonicalTreeConnectorRectangle,
): boolean => {
  const left =
    rectangle.centerX -
    rectangle.width / 2;

  const right =
    rectangle.centerX +
    rectangle.width / 2;

  const top =
    rectangle.centerY -
    rectangle.height / 2;

  const bottom =
    rectangle.centerY +
    rectangle.height / 2;

  if (
    Math.abs(start.x - end.x) <=
    TOLERANCE
  ) {
    const x = start.x;
    const minY =
      Math.min(start.y, end.y);
    const maxY =
      Math.max(start.y, end.y);

    return (
      x > left + TOLERANCE &&
      x < right - TOLERANCE &&
      maxY > top + TOLERANCE &&
      minY < bottom - TOLERANCE
    );
  }

  if (
    Math.abs(start.y - end.y) <=
    TOLERANCE
  ) {
    const y = start.y;
    const minX =
      Math.min(start.x, end.x);
    const maxX =
      Math.max(start.x, end.x);

    return (
      y > top + TOLERANCE &&
      y < bottom - TOLERANCE &&
      maxX > left + TOLERANCE &&
      minX < right - TOLERANCE
    );
  }

  throw new Error(
    'Lateral connector segments must remain orthogonal.',
  );
};

const intersectingObstacleIds = (
  points:
    readonly CanonicalTreeConnectorPoint[],
  obstacles:
    readonly CanonicalTreeConnectorRectangle[],
): string[] => {
  const rejected =
    new Set<string>();

  for (
    let index = 1;
    index < points.length;
    index += 1
  ) {
    const start = points[index - 1];
    const end = points[index];

    for (const obstacle of obstacles) {
      if (
        segmentIntersectsRectangle(
          start,
          end,
          obstacle,
        )
      ) {
        rejected.add(
          obstacle.memberId,
        );
      }
    }
  }

  return Array.from(rejected).sort();
};

export const planCanonicalTreeLateralConnector = (
  input:
    CanonicalTreeLateralConnectorInput,
): CanonicalTreeLateralConnectorResult => {
  const from =
    Object.freeze({
      x:
        finite(input.from.x, 'from.x'),
      y:
        finite(input.from.y, 'from.y'),
    });

  const to =
    Object.freeze({
      x:
        finite(input.to.x, 'to.x'),
      y:
        finite(input.to.y, 'to.y'),
    });

  const cardWidth =
    nonNegative(
      input.cardWidth,
      'cardWidth',
    );

  const cardHeight =
    nonNegative(
      input.cardHeight,
      'cardHeight',
    );

  const clearance =
    nonNegative(
      input.clearance,
      'clearance',
    );

  const direction =
    to.x >= from.x ? 1 : -1;

  const fromExit =
    Object.freeze({
      x:
        from.x +
        direction * cardWidth / 2,
      y:
        from.y,
    });

  const toEntry =
    Object.freeze({
      x:
        to.x -
        direction * cardWidth / 2,
      y:
        to.y,
    });

  const obstacles =
    input.obstacles.filter(
      obstacle =>
        obstacle.memberId !==
          input.fromMemberId &&
        obstacle.memberId !==
          input.toMemberId,
    );

  let lastRejected:
    string[] = [];

  for (
    let attempt = 0;
    attempt < MAX_ATTEMPTS;
    attempt += 1
  ) {
    const corridorOffset =
      cardHeight / 2 +
      clearance +
      attempt * clearance;

    const corridorY =
      Math.min(from.y, to.y) -
      corridorOffset;

    const points =
      Object.freeze([
        fromExit,
        Object.freeze({
          x:
            fromExit.x,
          y:
            corridorY,
        }),
        Object.freeze({
          x:
            toEntry.x,
          y:
            corridorY,
        }),
        toEntry,
      ]);

    const rejected =
      intersectingObstacleIds(
        points,
        obstacles,
      );

    if (rejected.length === 0) {
      return Object.freeze({
        status:
          'ROUTED',
        key:
          input.key,
        root:
          input.root,
        fromMemberIds:
          Object.freeze([
            input.fromMemberId,
          ]),
        toMemberId:
          input.toMemberId,
        points,
        corridorY,
        rejectedObstacleMemberIds:
          Object.freeze([]),
      });
    }

    lastRejected = rejected;
  }

  return Object.freeze({
    status:
      'REJECTED-OBSTACLE-INTERSECTION',
    key:
      input.key,
    root:
      input.root,
    fromMemberIds:
      Object.freeze([
        input.fromMemberId,
      ]),
    toMemberId:
      input.toMemberId,
    points:
      Object.freeze([]),
    corridorY:
      null,
    rejectedObstacleMemberIds:
      Object.freeze(lastRejected),
  });
};
